/**
 * @fileoverview Main Operator Administration Panel for FanMessages (Mensajes a la afición).
 * Allows stadium operators to create, edit, duplicate, delete and control projection
 * of personalized fan messages across stadium overlays with real-time live preview.
 * 
 * @module components/Admin/Games/FanMessages
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { Confirm } from './../../Commons/Modals/Confirm';
import { FanMessageModal } from './FanMessages/FanMessageModal';
import { FanMessagePreview, FAN_MESSAGE_FORMATS } from './FanMessages/FanMessagePreview';

import {
    BLGetMessages,
    BLDeleteMessage,
    BLDuplicateMessage,
    BLGetGlobalConfig
} from './../../../utilities/BL/FanMessages';

import {
    ConnectWebSocketForFanMessagesAdmin,
    DisconnectWebSocketForFanMessagesAdmin
} from './../../../utilities/WS/Admin/FanMessagesWS';

/**
 * FanMessages Operator Administration Component.
 * 
 * @function FanMessages
 * @param {Object} props - Component properties.
 * @param {Function} props.getOutOfTheDynamic - Callback to exit dynamic back to dashboard.
 * @returns {React.ReactElement} Admin panel markup.
 */
export const FanMessages = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // Loading & Network state
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [lastRefreshString, setLastRefreshString] = useState('');
    const [webSocketStatus, setWebSocketStatus] = useState('connecting');

    // Messages list & active projection state
    const [messagesList, setMessagesList] = useState([]);
    const [selectedMessageId, setSelectedMessageId] = useState(0);
    const [globalProjectionConfig, setGlobalProjectionConfig] = useState({
        activeMessageId: null,
        isVisible: false,
        globalOffsetY: 0,
        targetOverlay: 'all'
    });

    // Modals state
    const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
    const [messageToEdit, setMessageToEdit] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmModalConfig, setConfirmModalConfig] = useState({
        message: '',
        actionToExecute: null,
        yesButtonText: 'Sí, confirmar',
        noButtonText: 'Cancelar'
    });

    // Selected message object for preview
    const activeSelectedMessage = messagesList.find((msg) => msg.id === Number(selectedMessageId)) || messagesList[0] || null;

    /**
     * Handles real-time WebSocket payloads.
     */
    const handleWebSocketMessage = useCallback((packet) => {
        if (!packet || typeof packet !== 'object') return;

        if (packet.type === 'fanmessages') {
            if (packet.action === 'show' || packet.action === 'hide' || packet.action === 'config') {
                loadGlobalConfiguration();
            }
        }
    }, []);

    /**
     * Initializes component data and WebSocket listeners.
     */
    useEffect(() => {
        loadAllData();

        ConnectWebSocketForFanMessagesAdmin(
            handleWebSocketMessage,
            (status) => setWebSocketStatus(status)
        );

        return () => {
            DisconnectWebSocketForFanMessagesAdmin();
        };
    }, [handleWebSocketMessage]);

    /**
     * Loads messages and in-memory global projection state.
     */
    const loadAllData = async () => {
        setIsLoadingData(true);
        try {
            // 1. Fetch messages
            const messagesResponse = await BLGetMessages();
            if (messagesResponse.success && Array.isArray(messagesResponse.data)) {
                setMessagesList(messagesResponse.data);
                if (messagesResponse.data.length > 0 && selectedMessageId === 0) {
                    setSelectedMessageId(messagesResponse.data[0].id);
                }
            }

            // 2. Fetch global config
            await loadGlobalConfiguration();
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al consultar los mensajes a la afición.' });
        } finally {
            setIsLoadingData(false);
            setLastRefreshString(
                new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            );
        }
    };

    /**
     * Fetches the global projection status from the server.
     */
    const loadGlobalConfiguration = async () => {
        try {
            const configResponse = await BLGetGlobalConfig();
            if (configResponse.success && configResponse.data) {
                setGlobalProjectionConfig(configResponse.data);
                if (configResponse.data.activeMessageId && configResponse.data.activeMessageId > 0) {
                    setSelectedMessageId(configResponse.data.activeMessageId);
                }
            }
        } catch (error) {
            // Silent catch
        }
    };

    // ==========================================
    // CRUD ACTIONS
    // ==========================================

    const handleOpenCreateModal = () => {
        setMessageToEdit(null);
        setIsMessageModalOpen(true);
    };

    const handleOpenEditModal = (messageItem) => {
        setMessageToEdit(messageItem);
        setIsMessageModalOpen(true);
    };

    const handleDuplicateMessage = async (messageId) => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Duplicando mensaje...' });
        try {
            const dupRes = await BLDuplicateMessage(messageId);
            if (dupRes.success && dupRes.data) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mensaje duplicado correctamente.' });
                await loadAllData();
                setSelectedMessageId(dupRes.data.id);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: dupRes.message || 'Error al duplicar mensaje.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al duplicar mensaje.' });
        }
    };

    const handleConfirmDeleteMessage = (messageItem) => {
        setConfirmModalConfig({
            message: `¿Estás seguro de que deseas eliminar el mensaje "${messageItem.title}"?`,
            yesButtonText: 'Sí, eliminar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Eliminando mensaje...' });
                const deleteRes = await BLDeleteMessage(messageItem.id);
                if (deleteRes.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mensaje eliminado exitosamente.' });
                    await loadAllData();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: deleteRes.message || 'Error al eliminar.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            {/* Modal de Confirmación */}
            {isConfirmModalOpen && (
                <Confirm
                    message={confirmModalConfig.message}
                    actionToYesButton={confirmModalConfig.actionToExecute}
                    textYesButton={confirmModalConfig.yesButtonText}
                    textNoButton={confirmModalConfig.noButtonText}
                    setShowModal={setIsConfirmModalOpen}
                />
            )}

            {/* Modal de Creación / Edición */}
            {isMessageModalOpen && (
                <FanMessageModal
                    isOpen={isMessageModalOpen}
                    initialMessageData={messageToEdit}
                    onCloseModal={() => setIsMessageModalOpen(false)}
                    onMessageSavedSuccessfully={(savedMsg) => {
                        loadAllData();
                        if (savedMsg?.id) setSelectedMessageId(savedMsg.id);
                    }}
                />
            )}

            {/* HEADER DEL PANEL */}
            <div className="mb-4 text-center text-dark">
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <i className="bi bi-chat-square-quote-fill text-warning fs-1"></i>
                    <h2 className="text-uppercase fw-bold m-0" style={{ letterSpacing: '2px' }}>
                        Mensajes a la afición
                    </h2>
                </div>
                <p className="text-muted small mt-1 mb-2">
                    Gestión modular de cintillos y avisos institucionales para pantallas de estadio y transmisiones.
                </p>
            </div>

            {/* ============================================================ */}
            {/* BARRA SUPERIOR DE ACCIONES Y ESTADO                          */}
            {/* ============================================================ */}
            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3 p-3 bg-white rounded-4 shadow-sm border">
                <div className="d-flex align-items-center gap-3">
                    <button
                        type="button"
                        className="btn btn-primary fw-bold px-4 py-2 shadow-sm d-flex align-items-center gap-2"
                        onClick={handleOpenCreateModal}
                    >
                        <i className="bi bi-plus-circle-fill fs-5"></i>
                        <span>Crear Nuevo Mensaje</span>
                    </button>

                    {messagesList.length > 0 && (
                        <div className="d-flex align-items-center gap-2 border-start ps-3">
                            <label htmlFor="selectPreviewMessage" className="small text-muted fw-bold mb-0">
                                Previsualizar:
                            </label>
                            <select
                                id="selectPreviewMessage"
                                className="form-select form-select-sm fw-bold border-primary shadow-sm"
                                style={{ minWidth: '240px', maxWidth: '360px' }}
                                value={selectedMessageId || ''}
                                onChange={(e) => setSelectedMessageId(Number(e.target.value))}
                            >
                                {messagesList.map((msg) => (
                                    <option key={msg.id} value={msg.id}>
                                        #{msg.id} - {msg.title} (Fmt #{msg.formatId})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div className="d-flex align-items-center gap-3">
                    <span className="small text-muted">
                        WebSocket:{' '}
                        {webSocketStatus === 'connected' && <strong className="text-success">🟢 En Línea</strong>}
                        {webSocketStatus === 'connecting' && <strong className="text-warning">🟡 Conectando...</strong>}
                        {webSocketStatus === 'reconnecting' && <strong className="text-warning">🟡 Reconectando...</strong>}
                        {webSocketStatus === 'error' && <strong className="text-danger">🔴 Desconectado</strong>}
                    </span>
                    <span className="small text-muted border-start ps-3">
                        Actualizado: <strong className="text-dark">{lastRefreshString}</strong>
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-light border rounded-circle p-1 d-flex align-items-center justify-content-center"
                        onClick={loadAllData}
                        title="Refrescar datos"
                        style={{ width: '28px', height: '28px' }}
                    >
                        <i className={`bi bi-arrow-repeat text-primary ${isLoadingData ? 'spinner-border spinner-border-sm border-0' : ''}`}></i>
                    </button>
                </div>
            </div>

            {/* ============================================================ */}
            {/* SECCIÓN 1: LIVE PREVIEW DEL MENSAJE SELECCIONADO             */}
            {/* ============================================================ */}
            <div className="mb-4">
                {activeSelectedMessage ? (
                    <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
                        <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-eye-fill text-warning fs-5"></i>
                                <h6 className="m-0 fw-bold">
                                    Previsualización en Vivo: <span className="text-warning">"{activeSelectedMessage.title}"</span>
                                </h6>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
                                    onClick={() => handleOpenEditModal(activeSelectedMessage)}
                                >
                                    <i className="bi bi-pencil-square"></i> Editar este mensaje
                                </button>
                            </div>
                        </div>
                        <div className="card-body p-3 bg-dark">
                            <FanMessagePreview
                                message={activeSelectedMessage}
                                mode="modal"
                                showContainerFrame={false}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="card shadow-sm border-0 rounded-4 p-5 text-center d-flex align-items-center justify-content-center bg-dark text-white">
                        <i className="bi bi-chat-square-quote text-warning fs-1 mb-3"></i>
                        <h5 className="fw-bold">No hay mensajes registrados en el catálogo</h5>
                        <p className="small text-white-50 mb-3">Crea un mensaje nuevo para visualizarlo aquí y tenerlo disponible en cabina.</p>
                        <button
                            type="button"
                            className="btn btn-warning fw-bold text-dark px-4"
                            onClick={handleOpenCreateModal}
                        >
                            <i className="bi bi-plus-lg me-1"></i> Crear Primer Mensaje
                        </button>
                    </div>
                )}
            </div>

            {/* ============================================================ */}
            {/* SECCIÓN 2: GRID / TABLA DE MENSAJES GUARDADOS                */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 overflow-hidden mb-4">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-list-stars text-warning fs-5"></i>
                        <h5 className="m-0 fw-bold">Catálogo de Mensajes Guardados ({messagesList.length})</h5>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th style={{ width: '60px' }} className="text-center">#</th>
                                <th style={{ width: '200px' }}>Título</th>
                                <th style={{ width: '200px' }}>Formato</th>
                                <th>Contenido a Proyectar</th>
                                <th style={{ width: '120px' }} className="text-center">Tipografía</th>
                                <th style={{ width: '180px' }} className="text-end pe-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {messagesList.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-5 text-muted">
                                        <i className="bi bi-inbox fs-1 d-block mb-2 text-secondary"></i>
                                        No hay mensajes guardados en el sistema. ¡Crea el primero!
                                    </td>
                                </tr>
                            ) : (
                                messagesList.map((msgItem) => {
                                    const formatInfo = FAN_MESSAGE_FORMATS.find((f) => f.id === msgItem.formatId);
                                    const isCurrentlyProjecting =
                                        globalProjectionConfig.isVisible &&
                                        globalProjectionConfig.activeMessageId === msgItem.id;
                                    const isSelectedInPreview = selectedMessageId === msgItem.id;

                                    return (
                                        <tr
                                            key={msgItem.id}
                                            className={`${isSelectedInPreview ? 'table-primary' : ''} ${isCurrentlyProjecting ? 'table-warning' : ''}`}
                                        >
                                            <td className="text-center fw-bold text-secondary">
                                                {msgItem.id}
                                            </td>
                                            <td>
                                                <div className="fw-bold text-dark text-truncate" style={{ maxWidth: '200px' }}>
                                                    {msgItem.title}
                                                </div>
                                                {isCurrentlyProjecting && (
                                                    <span className="badge bg-danger small mt-1 animate-pulse">
                                                        🔥 En Pantalla
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className="badge bg-dark text-wrap text-start">
                                                    <i className={`bi ${formatInfo?.icon || 'bi-layout-text-window'} text-warning me-1`}></i>
                                                    {formatInfo?.label || `Formato #${msgItem.formatId}`}
                                                </span>
                                            </td>
                                            <td>
                                                {msgItem.subtitleText && (
                                                    <div className="small text-muted text-truncate" style={{ maxWidth: '300px' }}>
                                                        <strong>Sub:</strong> {msgItem.subtitleText}
                                                    </div>
                                                )}
                                                <div className="fw-semibold text-dark text-truncate" style={{ maxWidth: '350px' }}>
                                                    "{msgItem.mainText}"
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-secondary font-monospace">
                                                    {msgItem.fontFamily}
                                                </span>
                                            </td>
                                            <td className="text-end pe-3">
                                                <div className="btn-group btn-group-sm shadow-sm" role="group">
                                                    {/* Previsualizar en panel */}
                                                    <button
                                                        type="button"
                                                        className={`btn ${isSelectedInPreview ? 'btn-primary' : 'btn-outline-primary'}`}
                                                        onClick={() => setSelectedMessageId(msgItem.id)}
                                                        title="Previsualizar mensaje arriba"
                                                    >
                                                        <i className="bi bi-eye"></i>
                                                    </button>

                                                    {/* Editar */}
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-dark"
                                                        onClick={() => handleOpenEditModal(msgItem)}
                                                        title="Editar mensaje"
                                                    >
                                                        <i className="bi bi-pencil-square"></i>
                                                    </button>

                                                    {/* Duplicar */}
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-info"
                                                        onClick={() => handleDuplicateMessage(msgItem.id)}
                                                        title="Duplicar mensaje"
                                                    >
                                                        <i className="bi bi-copy text-dark"></i>
                                                    </button>

                                                    {/* Eliminar */}
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger"
                                                        onClick={() => handleConfirmDeleteMessage(msgItem)}
                                                        title="Eliminar mensaje"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </SecondaryBase>
    );
};
